import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from "react";
import { User } from "../interfaces/api.interfaces";
import { authApi } from "../services/auth.api";
import { broadcastLogout, subscribeAuthEvents } from "../utils/authBroadcast";
import { setSentryUser } from "../config/sentry";
import { clearCachedLists } from "../hooks/useCachedList";

interface AuthContextData {
  user: User | null;
  token: string | null;
  loading: boolean; // ← nuevo
  login: (token: string, user: User, remember?: boolean) => void;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextData | undefined>(undefined);
export { AuthContext };

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState(() => {
    // Guard contra storage corrupto: si alguien escribió manualmente
    // a `localStorage.user` (devtools) o si una versión antigua del
    // app dejó shape distinto, JSON.parse lanzaba y el provider
    // crasheaba al montar — la app entera quedaba en blanco. Detectado
    // por AuthContext.test.tsx (Fase 12 D, 2026-04-28).
    const tryParse = (raw: string | null): User | null => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };
    return tryParse(localStorage.getItem("user")) ?? tryParse(sessionStorage.getItem("user"));
  });
  // Round 2026-04-26 fase 6 (P1-B): el JWT vive solo en memoria + cookie
  // HttpOnly. Lo seguimos exponiendo en `token` porque el WebSocket lo
  // pasa vía `Sec-WebSocket-Protocol` ("bearer, <token>"), pero ya NO se
  // persiste en localStorage/sessionStorage (era robable por XSS). En
  // refresh, el usuario ve el último `user` cacheado mientras el backend
  // valida la cookie HttpOnly; si no hay sesión válida, las APIs disparan
  // forceLogout vía el interceptor 401.
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); // ← nuevo

  useEffect(() => {
    // Hidratación de sesión — round 2026-04-27.
    //
    // El JWT solo vive en memoria (fase 6 P1-B), pero la cookie HttpOnly
    // `authToken` es cross-tab y persiste tras refresh. Pedimos a
    // `/api/auth/me` un JWT fresco usando solo la cookie. Si responde
    // 200 → seteamos token en memoria + refrescamos user. Si responde
    // 401 (cookie expirada/ausente) → limpiamos cache y ProtectedRoute
    // manda a /login. El interceptor global de fetch tiene una excepción
    // específica para /auth/me para que el 401 no dispare redirect
    // (necesario porque /login mismo monta este context).
    //
    // Sin esto, abrir un módulo en otra tab arranca con `token=null` y
    // ProtectedRoute redirige a /login aunque la cookie siga válida.
    //
    // Mantenemos `loading=true` hasta que /me responda, siempre. Si lo
    // liberamos antes, ProtectedRoute ve `!token` y redirige al login
    // antes de que la rehidratación termine.
    let cancelled = false;

    // Mostramos el user cacheado de inmediato (cosmético, evita flash
    // del header/sidebar) — no afecta autorización porque `loading=true`
    // bloquea ProtectedRoute hasta tener token.
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem("user");
        sessionStorage.removeItem("user");
      }
    }

    (async () => {
      const data = await authApi.me();
      if (cancelled) return;

      if (data) {
        setToken(data.token);
        const fresh: User = data.user as unknown as User;
        setUser(fresh);
        // Mantenemos el storage que estaba activo (local=remember-me,
        // session=no). Si no había ninguno (nueva tab sin cache pero con
        // cookie válida), default a sessionStorage para no escalar el
        // alcance de la sesión sin consentimiento explícito.
        if (localStorage.getItem("user")) {
          localStorage.setItem("user", JSON.stringify(fresh));
        } else {
          sessionStorage.setItem("user", JSON.stringify(fresh));
        }
      } else {
        // 401 o red caída: limpiamos cualquier cache. ProtectedRoute
        // mandará a /login al ver token=null.
        setUser(null);
        localStorage.removeItem("user");
        sessionStorage.removeItem("user");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sentry — Round C 2026-04-28. Identificamos al usuario actual en
  // Sentry para que los errores del dashboard se correlacionen con
  // un user concreto. Sin SENTRY_DSN, setSentryUser es no-op.
  // No mandamos email — el id + rol son suficientes para correlacionar
  // sin exponer PII (decisión documentada en config/sentry.ts).
  useEffect(() => {
    if (user) {
      setSentryUser({ id: user.id, rol: user.rol });
    } else {
      setSentryUser(null);
    }
  }, [user]);

  // Suscripción al canal de auth — round 2026-04-27. Si otra tab del
  // mismo origin cierra sesión (logout manual o forceLogout por 401),
  // limpiamos esta tab también y redirigimos a /login para evitar UI
  // logueada huérfana. La cookie HttpOnly ya fue borrada por la tab que
  // emitió el logout, así que no tiene sentido pedir /me; vamos directo
  // a la pantalla de login.
  useEffect(() => {
    const unsubscribe = subscribeAuthEvents((msg) => {
      if (msg.type === "logout") {
        setToken(null);
        setUser(null);
        localStorage.removeItem("user");
        sessionStorage.removeItem("user");
        // Barre los caches de listas ("cache:*") — defensa en profundidad
        // para que no queden datos colgados entre sesiones. Round 2026-07-02.
        clearCachedLists();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    });
    return unsubscribe;
  }, []);

  // Funciones estabilizadas con useCallback para que el `value` del
  // Provider no cambie de identidad en cada render — los consumidores
  // de useAuth() re-renderizaban innecesariamente cada vez que el padre
  // (App.tsx) se renderizaba, aunque user/token no hubieran cambiado.
  const login = useCallback((newToken: string, newUser: User, remember = false) => {
    setToken(newToken);
    setUser(newUser);
    if (remember) {
      localStorage.setItem("user", JSON.stringify(newUser));
      sessionStorage.removeItem("user");
    } else {
      sessionStorage.setItem("user", JSON.stringify(newUser));
      localStorage.removeItem("user");
    }
  }, []);

  const logout = useCallback(() => {
    // ⚠️ ESTA LLAMADA ES LO QUE REALMENTE CIERRA LA SESIÓN. Round 2026-07-27.
    //
    // Hasta este round `logout()` solo limpiaba el estado de React y los
    // storages, y la cookie HttpOnly `authToken` —que es la credencial de
    // verdad, y que JS no puede borrar— sobrevivía hasta expirar (24 h).
    // Consecuencia reportada por el dueño: cerraba sesión, abría un enlace a
    // /finanzas-cliente y entraba igual, porque la hidratación del context
    // pide `/auth/me` con esa cookie y el backend le devuelve un JWT fresco.
    // Es decir: "cerrar sesión" no cerraba nada del lado del servidor.
    //
    // `authApi.logout()` pega a `POST /api/auth/logout` (el backend borra
    // ambas cookies) y limpia el csrf en memoria. Es fire-and-forget: si la
    // red falla, igual limpiamos todo lo local.
    authApi.logout();
    setToken(null);
    setUser(null);
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    // Barre los caches de listas ("cache:*") de useCachedList — defensa en
    // profundidad para que no queden datos colgados entre sesiones.
    clearCachedLists();
    // Avisamos a las demás tabs del mismo origin — round 2026-04-27.
    // BroadcastChannel no entrega el mensaje al emisor, así que no hay
    // loop con el listener de abajo.
    broadcastLogout();
  }, []);

  const updateUser = useCallback((data: Partial<User>) => {
    setUser((prev: User | null) => {
      if (!prev) return prev;
      const updated = { ...prev, ...data };
      // Persist to whichever storage is active
      if (localStorage.getItem("user")) {
        localStorage.setItem("user", JSON.stringify(updated));
      } else if (sessionStorage.getItem("user")) {
        sessionStorage.setItem("user", JSON.stringify(updated));
      }
      return updated;
    });
  }, []);

  // Memoizamos el value para no crear un objeto nuevo en cada render —
  // mismo patron que WebSocketContext (cerrado 2026-04-24).
  const value = useMemo(
    () => ({ user, token, loading, login, logout, updateUser }),
    [user, token, loading, login, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextData => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth debe usarse dentro de AuthProvider");
  }
  return ctx;
};
